"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useBrowserMinuteClock } from "@/lib/hooks/use-minute-clock";
import { judgeAreaClock, type AreaClockSport, type AreaJudgedRanks } from "./area-clock";

/**
 * The area pages are statically rendered (ISR), so every named score is judged at its render and
 * the HTML can be served after a spot's local midnight. One provider at the top of the page judges
 * each sport's ranked rows' carried row stamps (lib/areas/rows.ts getAreaBoardWithFreshness) again
 * on the browser's minute clock after hydration, as the board table does. Every surface that
 * prints a rank or today's score for a row (the ranked board, the heatmap's rank and today
 * cells, the map's numbered dots, the secondary sport's today cells) reads it by sport and slug:
 * a row whose board entry stops being the spot's current score loses its rank and number, and the
 * rows still current are ranked again with the same rule (rankAreaRows). The first client render
 * equals the server's, and it only ever withdraws.
 *
 * The provider takes only what re-ranking needs (`AreaClockRow`, area-clock.ts), once per sport,
 * so the client payload stays small however many surfaces read it.
 */

const AreaRankContext = createContext<ReadonlyMap<string, AreaJudgedRanks> | null>(null);

export function AreaRankClock({
  sports,
  children,
}: Readonly<{
  /** By toggle sport key. */
  sports: Readonly<Record<string, AreaClockSport>>;
  children?: ReactNode;
}>) {
  const nowMs = useBrowserMinuteClock();
  const judged = useMemo((): ReadonlyMap<string, AreaJudgedRanks> | null => {
    if (nowMs === null) return null;
    const out = new Map<string, AreaJudgedRanks>();
    for (const [key, sport] of Object.entries(sports)) {
      const ranks = judgeAreaClock(sport, nowMs);
      if (ranks) out.set(key, ranks);
    }
    return out.size > 0 ? out : null;
  }, [nowMs, sports]);
  return <AreaRankContext.Provider value={judged}>{children}</AreaRankContext.Provider>;
}

function useJudged(sport: string, slug: string) {
  return useContext(AreaRankContext)?.get(sport)?.get(slug);
}

/**
 * A ranked row's rank, judged again on the browser's clock inside AreaRankClock. `bare` prints the
 * number alone (inside SVG text, or where the surrounding label already says "rank").
 */
export function AreaRowRank({ sport, slug, rank, bare = false }: Readonly<{ sport: string; slug: string; rank: number | null; bare?: boolean }>) {
  const judged = useJudged(sport, slug);
  const shown = judged ? judged.rank : rank;
  if (shown === null) return null;
  return bare ? <>{shown}</> : <><span className="sr-only">Rank </span>{shown}</>;
}

/** The children while the row is still current on the browser's clock, else the fallback. */
export function AreaRowWhileCurrent({
  sport,
  slug,
  children,
  fallback = null,
}: Readonly<{ sport: string; slug: string; children?: ReactNode; fallback?: ReactNode }>) {
  const judged = useJudged(sport, slug);
  return judged && !judged.current ? fallback : children;
}
